import { useState, useEffect } from 'react';
import { get } from '../../../api/client';

export interface MonthlyData {
  month: string;
  key: string;
  income: number;
  expense: number;
  orders: number;
  completed: number;
  avgIncomeValue: number;
}

export interface ServiceTypeData {
  id: string;
  name: string;
  count: number;
  amount: number;
}

export interface CustomerRank {
  id: string;
  name: string;
  amount: number;
  orders: number;
}

export interface StatusData {
  name: string;
  value: number;
  color: string;
}

export interface AnalyticsData {
  totalIncome: number;
  totalExpense: number;
  netProfit: number;
  totalOrders: number;
  completedOrders: number;
  avgOrderValue: number;
  thisMonthIncome: number;
  lastMonthIncome: number;
  thisMonthOrders: number;
  lastMonthOrders: number;
  monthly: MonthlyData[];
  serviceTypes: ServiceTypeData[];
  customerRanks: CustomerRank[];
  statusBreakdown: StatusData[];
  /** 参与本次聚合的订单条数，用于核对与排障 */
  scannedOrders: number;
}

/**
 * 状态配色属于呈现层，留在前端；服务端只给「哪个状态、多少单」。
 */
const STATUS_COLORS: Record<string, string> = {
  未执行: '#f59e0b',
  执行中: '#06b6d4',
  已完成: '#10b981',
  已取消: '#9ca3af',
  已归档: '#a78bfa',
};

/** 后端 GET /orders/analytics 的响应（经全局拦截器 snake→camel）；状态项不含颜色 */
type AnalyticsPayload = Omit<AnalyticsData, 'statusBreakdown'> & {
  statusBreakdown: { name: string; value: number }[];
};

export type AnalyticsError = {
  /** 服务端以 403 拒绝：当前账号没有 advanced_analytics 功能权限，页面据此渲染升级引导 */
  denied: boolean;
  message: string;
};

/**
 * 数据统计数据 Hook：读服务端聚合结果。
 *
 * 口径的唯一事实源在后端 order-analytics.ts（原先在这里用 pageSize=9999 拉全量订单自己 reduce）。
 * 迁移除了让会员墙真正生效，还修掉两处静默失真：投影里 serviceType.id 恒为 null、
 * 且不含 customerId，导致服务类型分布与客户 Top 榜各自塌缩成一个分组。
 *
 * 权限判定不在前端做：直接请求，由服务端回答能或不能。在客户端预判会出现两个问题——
 * 会员状态是异步拉取的，付费用户会先闪一下锁屏；而判定逻辑一旦与服务端漂移，
 * 就会出现「页面给了锁但接口其实通」或反之。403 是权威答案，直接按它渲染。
 *
 * @param dateFrom 起始日期（YYYY-MM-DD），可选
 * @param dateTo 结束日期（YYYY-MM-DD），可选
 */
export function useAnalyticsData(dateFrom?: string, dateTo?: string) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<AnalyticsError | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      setFailure(null);
      try {
        // query 不经大小写转换（RequestInterceptor 只转 body），键名与后端 @Query 逐字一致
        const params: Record<string, unknown> = {};
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        const resp = await get<AnalyticsPayload | AnalyticsPayload[]>('/orders/analytics', { params });
        if (cancelled) return;
        const payload = Array.isArray(resp) ? resp[0] : resp;
        setData({
          ...payload,
          statusBreakdown: (payload?.statusBreakdown ?? []).map(s => ({
            ...s,
            color: STATUS_COLORS[s.name] || '#9ca3af',
          })),
        });
      } catch (e: unknown) {
        if (cancelled) return;
        const err = e as { status?: number; message?: string };
        setFailure({
          denied: err?.status === 403,
          message: err?.message || '加载失败',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  return {
    data,
    loading,
    error: failure?.denied ? null : (failure?.message ?? null),
    denied: failure?.denied ?? false,
  };
}
